import { Volume2 } from "lucide-react";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/**
 * Round ▶ icon button. Plays Romanian audio for the bound word/phrase.
 * Used by the study card (with `useRomanianAudio(text)` driving an
 * autoplay-on-change loop) and by the word detail page (manual-only,
 * no autoplay).
 *
 * Click-only — no keyboard shortcut. The study flow has Space/Enter
 * already wired to reveal/grade, and the word detail page doesn't
 * need a hotkey for a one-off action.
 *
 * `e.stopPropagation()` is important on the study card: the flashcard
 * wrapper has its own click handler that fires reveal, and we don't
 * want a play-tap to accidentally also flip the card. Harmless on
 * pages that don't have an outer click handler.
 */
export function PlayAudioButton({ play, className }: { play: () => void; className?: string }) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        play();
      }}
      aria-label={t("study.play_audio")}
      className={cn(
        "inline-flex size-9 items-center justify-center rounded-full border border-border bg-surface text-text-2 transition-colors hover:bg-[color:var(--surface-2)] hover:text-text",
        className,
      )}
    >
      <Volume2 className="size-4" />
    </button>
  );
}
